import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminApi } from '../services/api';
import { formatBSDate } from '../utils/bs';
import toast from 'react-hot-toast';
import type { PlantWithUsers, UserWithPlant, Role, Plant, Generator, Transformer, AlarmType, AnomalyType } from '../types';

type Tab = 'plants' | 'users' | 'roles' | 'categories';

function Badge({ active }: { active: boolean }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
      active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
    }`}>{active ? 'Active' : 'Inactive'}</span>
  );
}

// ── Equipment panel (generators + transformer) per plant ──────────────────────

function EquipmentPanel({ plant, onClose }: { plant: PlantWithUsers; onClose: () => void }) {
  const qc = useQueryClient();
  const [addGenOpen, setAddGenOpen] = useState(false);
  const [editGen, setEditGen]       = useState<Generator | null>(null);
  const [editTx,  setEditTx]        = useState(false);

  const { data: genData }  = useQuery({
    queryKey: ['sa-generators', plant.id],
    queryFn: () => superAdminApi.listGenerators(plant.id).then(r => r.data),
  });
  const { data: txData } = useQuery({
    queryKey: ['sa-transformer', plant.id],
    queryFn: () => superAdminApi.getTransformer(plant.id).then(r => r.data),
  });

  const generators  = genData?.generators ?? [];
  const transformer = txData?.transformer ?? null;

  // Generator form
  const [genForm, setGenForm] = useState({ name: '', rated_power_kw: '', voltage_kv: '' });
  const genMut = useMutation({
    mutationFn: () => editGen
      ? superAdminApi.updateGenerator(plant.id, editGen.id, {
          name:           genForm.name || undefined,
          rated_power_kw: genForm.rated_power_kw ? Number(genForm.rated_power_kw) : undefined,
          voltage_kv:     genForm.voltage_kv     ? Number(genForm.voltage_kv)     : undefined,
        })
      : superAdminApi.createGenerator(plant.id, {
          name:           genForm.name,
          rated_power_kw: genForm.rated_power_kw ? Number(genForm.rated_power_kw) : undefined,
          voltage_kv:     genForm.voltage_kv     ? Number(genForm.voltage_kv)     : undefined,
        }),
    onSuccess: () => {
      toast.success(editGen ? 'Generator updated' : 'Generator added');
      qc.invalidateQueries({ queryKey: ['sa-generators', plant.id] });
      setAddGenOpen(false); setEditGen(null);
      setGenForm({ name: '', rated_power_kw: '', voltage_kv: '' });
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed'),
  });

  const toggleGenActive = (g: Generator) =>
    superAdminApi.updateGenerator(plant.id, g.id, { is_active: !g.is_active })
      .then(() => qc.invalidateQueries({ queryKey: ['sa-generators', plant.id] }));

  const openEditGen = (g: Generator) => {
    setEditGen(g);
    setGenForm({ name: g.name, rated_power_kw: String(g.rated_power_kw ?? ''), voltage_kv: String(g.voltage_kv ?? '') });
    setAddGenOpen(true);
  };

  // Transformer form
  const [txForm, setTxForm] = useState({
    name:          transformer?.name ?? 'Main Transformer',
    rated_mva:     transformer?.rated_mva != null ? String(transformer.rated_mva) : '',
    voltage_hv_kv: transformer?.voltage_hv_kv != null ? String(transformer.voltage_hv_kv) : '',
    voltage_lv_kv: transformer?.voltage_lv_kv != null ? String(transformer.voltage_lv_kv) : '',
  });
  const txMut = useMutation({
    mutationFn: () => superAdminApi.upsertTransformer(plant.id, {
      name:          txForm.name || undefined,
      rated_mva:     txForm.rated_mva     ? Number(txForm.rated_mva)     : undefined,
      voltage_hv_kv: txForm.voltage_hv_kv ? Number(txForm.voltage_hv_kv) : undefined,
      voltage_lv_kv: txForm.voltage_lv_kv ? Number(txForm.voltage_lv_kv) : undefined,
    }),
    onSuccess: () => {
      toast.success('Transformer saved');
      qc.invalidateQueries({ queryKey: ['sa-transformer', plant.id] });
      setEditTx(false);
    },
    onError: () => toast.error('Failed'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <div>
            <h2 className="text-base font-bold text-slate-900">Equipment — {plant.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">Generators and main transformer</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Generators */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-800">Generators ({generators.length})</h3>
              <button
                onClick={() => { setEditGen(null); setGenForm({ name:'', rated_power_kw:'', voltage_kv:'' }); setAddGenOpen(true); }}
                className="btn-primary text-xs py-1.5 px-3"
              >+ Add Generator</button>
            </div>

            {addGenOpen && (
              <div className="border border-brand-200 rounded-lg p-4 mb-3 bg-brand-50/30 space-y-3">
                <p className="text-xs font-semibold text-slate-700">{editGen ? 'Edit Generator' : 'New Generator'}</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-3 md:col-span-1">
                    <label className="block text-xs text-slate-500 mb-1">Name *</label>
                    <input className="input w-full text-sm" value={genForm.name} onChange={e => setGenForm(p=>({...p, name: e.target.value}))} placeholder="Generator Unit 1" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Rated Power (kW)</label>
                    <input type="number" className="input w-full text-sm" value={genForm.rated_power_kw} onChange={e => setGenForm(p=>({...p, rated_power_kw: e.target.value}))} placeholder="5000" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Voltage (kV)</label>
                    <input type="number" className="input w-full text-sm" value={genForm.voltage_kv} onChange={e => setGenForm(p=>({...p, voltage_kv: e.target.value}))} placeholder="11" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => genMut.mutate()} disabled={genMut.isPending || !genForm.name.trim()} className="btn-primary text-xs py-1.5 px-4">
                    {genMut.isPending ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => { setAddGenOpen(false); setEditGen(null); }} className="btn-ghost text-xs py-1.5 px-4">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {generators.map(g => (
                <div key={g.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-hydro-border bg-slate-50/50">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">⚡</span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{g.name}</p>
                      <p className="text-xs text-slate-500">
                        {g.rated_power_kw != null ? `${Number(g.rated_power_kw).toLocaleString()} kW` : '—'}
                        {g.voltage_kv != null ? ` · ${g.voltage_kv} kV` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge active={g.is_active} />
                    <button onClick={() => openEditGen(g)} className="text-xs text-brand-600 hover:underline">Edit</button>
                    <button onClick={() => toggleGenActive(g)} className="text-xs text-slate-400 hover:text-slate-600">
                      {g.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))}
              {generators.length === 0 && <p className="text-xs text-slate-400 text-center py-3">No generators added yet</p>}
            </div>
          </div>

          {/* Transformer */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-800">Main Transformer</h3>
              <button onClick={() => {
                setTxForm({
                  name: transformer?.name ?? 'Main Transformer',
                  rated_mva: transformer?.rated_mva != null ? String(transformer.rated_mva) : '',
                  voltage_hv_kv: transformer?.voltage_hv_kv != null ? String(transformer.voltage_hv_kv) : '',
                  voltage_lv_kv: transformer?.voltage_lv_kv != null ? String(transformer.voltage_lv_kv) : '',
                });
                setEditTx(true);
              }} className="btn-ghost text-xs py-1.5 px-3">{transformer ? 'Edit' : '+ Configure'}</button>
            </div>

            {editTx ? (
              <div className="border border-brand-200 rounded-lg p-4 bg-brand-50/30 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs text-slate-500 mb-1">Name</label>
                    <input className="input w-full text-sm" value={txForm.name} onChange={e => setTxForm(p=>({...p, name: e.target.value}))} />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Rated (MVA)</label>
                    <input type="number" className="input w-full text-sm" value={txForm.rated_mva} onChange={e => setTxForm(p=>({...p, rated_mva: e.target.value}))} placeholder="6.0" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">HV Voltage (kV)</label>
                    <input type="number" className="input w-full text-sm" value={txForm.voltage_hv_kv} onChange={e => setTxForm(p=>({...p, voltage_hv_kv: e.target.value}))} placeholder="33" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">LV Voltage (kV)</label>
                    <input type="number" className="input w-full text-sm" value={txForm.voltage_lv_kv} onChange={e => setTxForm(p=>({...p, voltage_lv_kv: e.target.value}))} placeholder="11" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => txMut.mutate()} disabled={txMut.isPending} className="btn-primary text-xs py-1.5 px-4">
                    {txMut.isPending ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditTx(false)} className="btn-ghost text-xs py-1.5 px-4">Cancel</button>
                </div>
              </div>
            ) : transformer ? (
              <div className="flex items-center gap-4 px-3 py-2.5 rounded-lg border border-hydro-border bg-slate-50/50">
                <span className="text-lg">🔌</span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{transformer.name}</p>
                  <p className="text-xs text-slate-500">
                    {transformer.rated_mva != null ? `${transformer.rated_mva} MVA` : '—'}
                    {transformer.voltage_hv_kv != null ? ` · ${transformer.voltage_hv_kv}/${transformer.voltage_lv_kv} kV` : ''}
                  </p>
                </div>
                <Badge active={transformer.is_active} />
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-3">No transformer configured</p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-hydro-border">
          <button onClick={onClose} className="btn-ghost w-full">Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Plant form modal ──────────────────────────────────────────────────────────

function PlantFormModal({ plant, onClose }: { plant?: PlantWithUsers; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!plant;
  const [form, setForm] = useState({
    name:            plant?.name ?? '',
    location:        plant?.location ?? '',
    capacity_kw:     plant?.capacity_kw != null ? String(plant.capacity_kw) : '',
    commissioned_at: plant?.commissioned_at ? String(plant.commissioned_at).slice(0, 10) : '',
    is_active:       plant?.is_active ?? true,
    scada_enabled:   plant?.scada_enabled ?? false,
    latitude:        plant?.latitude  != null ? String(plant.latitude)  : '',
    longitude:       plant?.longitude != null ? String(plant.longitude) : '',
  });

  const mutation = useMutation({
    mutationFn: () =>
      isEdit
        ? superAdminApi.updatePlant(plant!.id, {
            name: form.name, location: form.location || undefined,
            capacity_kw: form.capacity_kw ? Number(form.capacity_kw) : undefined,
            commissioned_at: form.commissioned_at || undefined,
            is_active: form.is_active, scada_enabled: form.scada_enabled,
            latitude:  form.latitude  ? Number(form.latitude)  : null,
            longitude: form.longitude ? Number(form.longitude) : null,
          })
        : superAdminApi.createPlant({
            name: form.name, location: form.location || undefined,
            capacity_kw: form.capacity_kw ? Number(form.capacity_kw) : undefined,
            commissioned_at: form.commissioned_at || undefined,
            scada_enabled: form.scada_enabled,
            latitude:  form.latitude  ? Number(form.latitude)  : undefined,
            longitude: form.longitude ? Number(form.longitude) : undefined,
          }),
    onSuccess: () => {
      toast.success(isEdit ? 'Plant updated' : 'Plant created');
      qc.invalidateQueries({ queryKey: ['sa-plants'] });
      onClose();
    },
    onError: () => toast.error('Save failed'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">{isEdit ? 'Edit Plant' : 'New Hydropower Plant'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Plant Name <span className="text-red-500">*</span></label>
            <input className="input w-full" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Hydro Plant Alpha" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Location</label>
            <input className="input w-full" value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="e.g. Himalayan Region, Unit 1" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Latitude</label>
              <input type="number" step="0.000001" className="input w-full" value={form.latitude} onChange={e => setForm(p => ({...p, latitude: e.target.value}))} placeholder="e.g. 30.720000" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Longitude</label>
              <input type="number" step="0.000001" className="input w-full" value={form.longitude} onChange={e => setForm(p => ({...p, longitude: e.target.value}))} placeholder="e.g. 77.100000" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Capacity (kW)</label>
              <input type="number" className="input w-full" value={form.capacity_kw} onChange={e => setForm(p => ({...p, capacity_kw: e.target.value}))} placeholder="5000" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Commissioned</label>
              <input type="date" className="input w-full" value={form.commissioned_at} onChange={e => setForm(p => ({...p, commissioned_at: e.target.value}))} />
            </div>
          </div>

          {/* SCADA toggle */}
          <div className={`rounded-lg border p-3 ${form.scada_enabled ? 'border-brand-400 bg-brand-50/40' : 'border-hydro-border'}`}>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                onClick={() => setForm(p => ({...p, scada_enabled: !p.scada_enabled}))}
                className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${form.scada_enabled ? 'bg-brand-600' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.scada_enabled ? 'left-5' : 'left-1'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">SCADA Enabled</p>
                <p className="text-xs text-slate-500">
                  {form.scada_enabled
                    ? 'Data pulled automatically from SCADA system'
                    : 'Data must be entered manually'}
                </p>
              </div>
            </label>
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({...p, is_active: e.target.checked}))} className="w-4 h-4 accent-brand-600" />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.name.trim()} className="btn-primary flex-1">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Plant'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Plants tab ────────────────────────────────────────────────────────────────

function PlantsTab() {
  const [modal,     setModal]     = useState<'create' | PlantWithUsers | null>(null);
  const [equipment, setEquipment] = useState<PlantWithUsers | null>(null);

  const { data, isLoading } = useQuery<{ plants: PlantWithUsers[] }>({
    queryKey: ['sa-plants'],
    queryFn: () => superAdminApi.listPlants().then(r => r.data),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{data?.plants.length ?? 0} plant(s) registered</p>
        <button onClick={() => setModal('create')} className="btn-primary text-sm">+ New Plant</button>
      </div>

      {isLoading ? (
        <div className="card text-center py-10 text-slate-400">Loading…</div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
                {['ID','Name','Location','Capacity','SCADA','Commissioned','Users','Status',''].map(h => (
                  <th key={h} className="px-4 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.plants.map(p => (
                <tr key={p.id} className="table-row text-sm">
                  <td className="px-4 py-3 font-mono text-slate-400 text-xs">{p.id}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{p.name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{p.location ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.capacity_kw != null ? `${Number(p.capacity_kw).toLocaleString()} kW` : '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.scada_enabled ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'}`}>
                      {p.scada_enabled ? '⚡ SCADA' : 'Manual'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {p.commissioned_at ? formatBSDate(new Date(p.commissioned_at)) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600">
                      <span className="text-base">👤</span>{p.user_count}
                    </span>
                  </td>
                  <td className="px-4 py-3"><Badge active={p.is_active} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => setModal(p)} className="text-xs text-brand-600 hover:underline">Edit</button>
                      <button onClick={() => setEquipment(p)} className="text-xs text-slate-500 hover:underline">Equipment</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal === 'create' && <PlantFormModal onClose={() => setModal(null)} />}
      {modal && modal !== 'create' && <PlantFormModal plant={modal as PlantWithUsers} onClose={() => setModal(null)} />}
      {equipment && <EquipmentPanel plant={equipment} onClose={() => setEquipment(null)} />}
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────

function UserFormModal({ user, plants, roles, onClose }: {
  user?: UserWithPlant; plants: PlantWithUsers[]; roles: Role[]; onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!user;
  const [form, setForm] = useState({
    name:     user?.name ?? '',
    email:    user?.email ?? '',
    password: '',
    role_id:  user?.role_id ?? 2,
    plant_id: user?.plant_id ?? null as number | null,
    is_active: user?.is_active ?? true,
  });

  const mutation = useMutation({
    mutationFn: () =>
      isEdit
        ? superAdminApi.updateUser(user!.id, {
            name: form.name || undefined, role_id: form.role_id, plant_id: form.plant_id,
            is_active: form.is_active, password: form.password || undefined,
          })
        : superAdminApi.createUser({ name: form.name, email: form.email, password: form.password, role_id: form.role_id, plant_id: form.plant_id }),
    onSuccess: () => {
      toast.success(isEdit ? 'User updated' : 'User created');
      qc.invalidateQueries({ queryKey: ['sa-users'] });
      qc.invalidateQueries({ queryKey: ['sa-plants'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Save failed';
      toast.error(msg);
    },
  });

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const showEmailError = !isEdit && form.email.length > 0 && !emailValid;

  const canSubmit = isEdit
    ? !!form.name && (!form.password || form.password.length >= 6)
    : !!form.name && emailValid && form.password.length >= 6;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">{isEdit ? 'Edit User' : 'New User'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className={isEdit ? 'col-span-2' : ''}>
              <label className="block text-xs text-slate-500 mb-1.5">Full Name <span className="text-red-500">*</span></label>
              <input className="input w-full" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="John Smith" />
            </div>
            {!isEdit && (
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input type="email" className={`input w-full ${showEmailError ? 'border-red-500' : ''}`} value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="user@example.com" />
                {showEmailError && <p className="text-xs text-red-500 mt-1">Enter a valid email address</p>}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">
              Password {isEdit ? <span className="text-slate-400">(leave blank to keep current)</span> : <span className="text-red-500">*</span>}
            </label>
            <input type="password" className="input w-full" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder="Min 6 characters" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Role <span className="text-red-500">*</span></label>
            <select className="input w-full" value={form.role_id} onChange={e => setForm(p => ({...p, role_id: Number(e.target.value)}))}>
              {roles.map(r => (
                <option key={r.id} value={r.id}>{r.name.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Assign to Plant</label>
            <select
              className="input w-full"
              value={form.plant_id ?? ''}
              onChange={e => setForm(p => ({...p, plant_id: e.target.value ? Number(e.target.value) : null}))}
            >
              <option value="">— No plant assigned —</option>
              {plants.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({...p, is_active: e.target.checked}))} className="w-4 h-4 accent-brand-600" />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending || !canSubmit} className="btn-primary flex-1">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [modal, setModal] = useState<'create' | UserWithPlant | null>(null);

  const { data: usersData, isLoading } = useQuery<{ users: UserWithPlant[] }>({
    queryKey: ['sa-users'],
    queryFn: () => superAdminApi.listUsers().then(r => r.data),
  });
  const { data: plantsData } = useQuery<{ plants: PlantWithUsers[] }>({
    queryKey: ['sa-plants'],
    queryFn: () => superAdminApi.listPlants().then(r => r.data),
  });
  const { data: rolesData } = useQuery<{ roles: Role[] }>({
    queryKey: ['sa-roles'],
    queryFn: () => superAdminApi.listRoles().then(r => r.data),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{usersData?.users.length ?? 0} user(s)</p>
        <button onClick={() => setModal('create')} className="btn-primary text-sm">+ New User</button>
      </div>
      {isLoading ? (
        <div className="card text-center py-10 text-slate-400">Loading…</div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
                {['Name','Email','Role','Plant','Last Login','Status',''].map(h => (
                  <th key={h} className="px-4 py-3 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usersData?.users.map(u => (
                <tr key={u.id} className="table-row text-sm">
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-brand-600/10 text-brand-700 font-medium capitalize">
                      {u.role.replace(/_/g,' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.plant
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{u.plant.name}</span>
                      : <span className="text-xs text-slate-400">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {u.last_login_at ? formatBSDate(new Date(u.last_login_at)) : 'Never'}
                  </td>
                  <td className="px-4 py-3"><Badge active={u.is_active} /></td>
                  <td className="px-4 py-3">
                    <button onClick={() => setModal(u)} className="text-xs text-brand-600 hover:underline">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal === 'create' && <UserFormModal plants={plantsData?.plants ?? []} roles={rolesData?.roles ?? []} onClose={() => setModal(null)} />}
      {modal && modal !== 'create' && <UserFormModal user={modal as UserWithPlant} plants={plantsData?.plants ?? []} roles={rolesData?.roles ?? []} onClose={() => setModal(null)} />}
    </div>
  );
}

// ── Roles tab ─────────────────────────────────────────────────────────────────

const ROLE_META: Record<string, { label: string; icon: string; description: string; tasks: string[] }> = {
  executive:     { label:'Executive / CEO',  icon:'👔', description:'Senior leadership with full read access to all operational and financial data.',
    tasks:['Board reporting — plant-wide KPIs and generation trends','Daily company monitoring — schedules, generation history, alarms','Access to all modules used by other roles'] },
  finance:       { label:'Finance Officer',  icon:'💰', description:'Financial reporting, power sales invoicing, and regulatory submissions.',
    tasks:['Generation invoicing for power sales (kWh × tariff rate)','Company finance reporting','IR Reporting — investor-ready generation summaries','ERC availability declarations (daily / weekly / monthly)'] },
  engineer:      { label:'Plant Engineer',   icon:'⚙️', description:'Operational engineering — forecasting, ERC compliance, maintenance scheduling.',
    tasks:['Daily plant operations monitoring','Production forecasting and dispatch planning','ERC Reporting — availability declarations','Maintenance and shutdown scheduling','Anomaly detection and health scoring','Historical data analysis'] },
  plant_operator:{ label:'Plant Operator',   icon:'🔧', description:'Hands-on daily operations including equipment control and fault logging.',
    tasks:['Daily operations and monitoring of plant','Operation of intake gate, generator and circuit breakers','Logging of operations, faults and alarms','Manual data entry when SCADA is offline','Reports to office (daily report)'] },
  admin:         { label:'Plant Admin',      icon:'🛠', description:'Plant-level administrator — SCADA settings, tag configuration, notifications.',
    tasks:['SCADA tag configuration and thresholds','Notification channel management','Full operational data access','User management within plant'] },
  operator:      { label:'Operator',         icon:'🖥', description:'General plant operator with full operational access (legacy role).',
    tasks:['Dashboard monitoring','Alarm management','Reports','Historical data','Hydrology'] },
  viewer:        { label:'Viewer',            icon:'👁', description:'Read-only access to dashboard and reports.',
    tasks:['Dashboard (read-only)','Reports (view only)'] },
  super_admin:   { label:'Super Admin',       icon:'🏭', description:'Platform-wide administrator across all plants and tenants.',
    tasks:['Multi-plant management','User creation and role assignment','All plant data access','Equipment and SCADA configuration'] },
};

const PERM_LABELS: Record<string, string> = {
  dashboard:'Dashboard', alarms:'Alarms', reports:'Reports', historical:'Historical',
  hydrology:'Hydrology', anomaly:'Anomaly', admin:'Admin Panel', super_admin:'Platform',
  planning:'Planning',
};

function RolesTab() {
  const { data: rolesData, isLoading } = useQuery<{ roles: Role[] }>({
    queryKey: ['sa-roles'],
    queryFn: () => superAdminApi.listRoles().then(r => r.data),
  });

  if (isLoading) return <div className="card text-center py-10 text-slate-400">Loading…</div>;
  const roles = rolesData?.roles ?? [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{roles.length} roles configured</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {roles.map(role => {
          const meta = ROLE_META[role.name];
          if (!meta) return null;
          return (
            <div key={role.id} className="card space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{meta.icon}</span>
                <div>
                  <p className="text-sm font-bold text-slate-900">{meta.label}</p>
                  <p className="text-xs text-slate-500">{meta.description}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Responsibilities</p>
                <ul className="space-y-0.5">
                  {meta.tasks.map(t => (
                    <li key={t} className="text-xs text-slate-600 flex items-start gap-1.5">
                      <span className="text-brand-500 mt-0.5 flex-shrink-0">›</span>{t}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Module Access</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(PERM_LABELS).map(([key, label]) => {
                    const has = !!(role.permissions as Record<string,boolean>)[key];
                    return (
                      <span key={key} className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                        has ? 'bg-emerald-50 text-emerald-700 border-emerald-300/60'
                            : 'bg-slate-100 text-slate-400 border-slate-200 line-through'
                      }`}>{label}</span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Categories tab ────────────────────────────────────────────────────────────

type TypeKind = 'alarm' | 'anomaly';

const ALARM_CATEGORIES  = ['mechanical','electrical','control','protection','communication'];
const ANOMALY_CATEGORIES = ['process','sensor','hydraulic','mechanical','electrical','environmental'];

function TypeFormModal({ kind, item, onClose }: {
  kind: TypeKind;
  item?: AlarmType | AnomalyType;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!item;
  const categories = kind === 'alarm' ? ALARM_CATEGORIES : ANOMALY_CATEGORIES;

  const [form, setForm] = useState({
    name:             item?.name             ?? '',
    category:         item?.category         ?? categories[0],
    default_severity: item?.default_severity ?? 'warning',
    description:      item?.description      ?? '',
    is_active:        item?.is_active        ?? true,
  });

  const mut = useMutation({
    mutationFn: (): Promise<unknown> => {
      const payload = {
        name:             form.name,
        category:         form.category,
        ...(isEdit && { default_severity: form.default_severity }),
        description:      form.description || undefined,
        is_active:        form.is_active,
      };
      if (kind === 'alarm') {
        return isEdit
          ? superAdminApi.updateAlarmType(item!.id, payload)
          : superAdminApi.createAlarmType(payload);
      } else {
        return isEdit
          ? superAdminApi.updateAnomalyType(item!.id, payload)
          : superAdminApi.createAnomalyType(payload);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['sa-alarm-types'] });
      qc.invalidateQueries({ queryKey: ['sa-anomaly-types'] });
      onClose();
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Failed'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-hydro-border">
          <h2 className="text-base font-bold text-slate-900">
            {isEdit ? 'Edit' : 'New'} {kind === 'alarm' ? 'Alarm' : 'Anomaly'} Type
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">×</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Name <span className="text-red-500">*</span></label>
            <input className="input w-full" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. Voltage Fluctuation" />
          </div>
          <div className={isEdit ? 'grid grid-cols-2 gap-4' : ''}>
            <div>
              <label className="block text-xs text-slate-500 mb-1.5">Category</label>
              <select className="input w-full capitalize" value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}>
                {categories.map(c => <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
              </select>
            </div>
            {isEdit && (
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">Default Severity</label>
                <select className="input w-full" value={form.default_severity} onChange={e => setForm(p => ({...p, default_severity: e.target.value as 'info'|'warning'|'critical'}))}>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Description</label>
            <textarea className="input w-full h-20 resize-none" value={form.description}
              onChange={e => setForm(p => ({...p, description: e.target.value}))}
              placeholder="Brief description of when this type occurs…" />
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({...p, is_active: e.target.checked}))} className="w-4 h-4 accent-brand-600" />
              <span className="text-sm text-slate-700">Active (appears in dropdowns)</span>
            </label>
          )}
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-hydro-border">
          <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.name.trim()} className="btn-primary flex-1">
            {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
          </button>
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TypeTable<T extends AlarmType | AnomalyType>({
  kind, items, onEdit, onDelete, onToggle,
}: {
  kind: TypeKind;
  items: T[];
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  onToggle: (item: T) => void;
}) {
  const SCLASS: Record<string, string> = {
    critical: 'badge-crit', warning: 'badge-warn', info: 'badge-info',
  };
  return (
    <div className="card p-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-hydro-border text-xs text-slate-500 uppercase tracking-wider">
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Default Severity</th>
              <th className="px-4 py-3 text-left">Description</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No {kind} types defined</td></tr>
            ) : items.map(item => (
              <tr key={item.id} className="table-row text-sm border-b border-hydro-border last:border-0">
                <td className="px-4 py-3 font-medium text-slate-900">{item.name}</td>
                <td className="px-4 py-3 text-xs capitalize text-slate-600">{item.category}</td>
                <td className="px-4 py-3"><span className={SCLASS[item.default_severity] ?? 'badge-info'}>{item.default_severity}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{item.description ?? '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => onEdit(item)} className="text-xs text-brand-600 hover:underline">Edit</button>
                    <button onClick={() => onToggle(item)} className="text-xs text-slate-400 hover:text-slate-600">
                      {item.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => onDelete(item)} className="text-xs text-red-400 hover:text-red-600">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const qc = useQueryClient();
  const [editItem, setEditItem]   = useState<AlarmType | AnomalyType | null>(null);
  const [editKind, setEditKind]   = useState<TypeKind>('alarm');
  const [addKind,  setAddKind]    = useState<TypeKind | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ kind: TypeKind; item: AlarmType | AnomalyType } | null>(null);

  const { data: alarmTypesData } = useQuery({
    queryKey: ['sa-alarm-types'],
    queryFn: () => superAdminApi.listAlarmTypes().then(r => r.data),
  });
  const { data: anomalyTypesData } = useQuery({
    queryKey: ['sa-anomaly-types'],
    queryFn: () => superAdminApi.listAnomalyTypes().then(r => r.data),
  });

  const alarmTypes  = alarmTypesData?.alarm_types  ?? [];
  const anomalyTypes = anomalyTypesData?.anomaly_types ?? [];

  const deleteMut = useMutation({
    mutationFn: ({ kind, item }: { kind: TypeKind; item: AlarmType | AnomalyType }): Promise<unknown> =>
      kind === 'alarm' ? superAdminApi.deleteAlarmType(item.id) : superAdminApi.deleteAnomalyType(item.id),
    onSuccess: (_, vars) => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: [vars.kind === 'alarm' ? 'sa-alarm-types' : 'sa-anomaly-types'] });
      setConfirmDelete(null);
    },
    onError: () => toast.error('Delete failed — type may be in use'),
  });

  const toggleMut = useMutation({
    mutationFn: ({ kind, item }: { kind: TypeKind; item: AlarmType | AnomalyType }): Promise<unknown> =>
      kind === 'alarm'
        ? superAdminApi.updateAlarmType(item.id, { is_active: !item.is_active })
        : superAdminApi.updateAnomalyType(item.id, { is_active: !item.is_active }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [vars.kind === 'alarm' ? 'sa-alarm-types' : 'sa-anomaly-types'] });
    },
    onError: () => toast.error('Failed'),
  });

  const openEdit = (kind: TypeKind, item: AlarmType | AnomalyType) => {
    setEditKind(kind);
    setEditItem(item);
  };

  return (
    <div className="space-y-8">
      {/* Alarm Types */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">🔔 Alarm Types</h3>
            <p className="text-xs text-slate-500 mt-0.5">{alarmTypes.length} types · {alarmTypes.filter(t => t.is_active).length} active</p>
          </div>
          <button onClick={() => setAddKind('alarm')} className="btn-primary text-sm">+ Add Type</button>
        </div>
        <TypeTable
          kind="alarm"
          items={alarmTypes}
          onEdit={item => openEdit('alarm', item)}
          onDelete={item => setConfirmDelete({ kind: 'alarm', item })}
          onToggle={item => toggleMut.mutate({ kind: 'alarm', item })}
        />
      </div>

      {/* Anomaly Types */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900">🧠 Anomaly Types</h3>
            <p className="text-xs text-slate-500 mt-0.5">{anomalyTypes.length} types · {anomalyTypes.filter(t => t.is_active).length} active</p>
          </div>
          <button onClick={() => setAddKind('anomaly')} className="btn-primary text-sm">+ Add Type</button>
        </div>
        <TypeTable
          kind="anomaly"
          items={anomalyTypes}
          onEdit={item => openEdit('anomaly', item)}
          onDelete={item => setConfirmDelete({ kind: 'anomaly', item })}
          onToggle={item => toggleMut.mutate({ kind: 'anomaly', item })}
        />
      </div>

      {/* Create modals */}
      {addKind && <TypeFormModal kind={addKind} onClose={() => setAddKind(null)} />}

      {/* Edit modals */}
      {editItem && <TypeFormModal kind={editKind} item={editItem} onClose={() => { setEditItem(null); }} />}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <p className="text-sm font-semibold text-slate-900">Delete "{confirmDelete.item.name}"?</p>
            <p className="text-xs text-slate-500">This cannot be undone. Types that have been used in logged alarms or anomalies cannot be deleted.</p>
            <div className="flex gap-3">
              <button onClick={() => deleteMut.mutate(confirmDelete)} disabled={deleteMut.isPending}
                className="flex-1 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium">
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </button>
              <button onClick={() => setConfirmDelete(null)} className="btn-ghost flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SuperAdmin() {
  const [tab, setTab] = useState<Tab>('plants');

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Platform Administration</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage hydropower plants, equipment, and users across the platform</p>
      </div>

      <div className="flex gap-2 border-b border-hydro-border pb-3">
        {([['plants','🏭 Plants'],['users','👥 Users'],['roles','🔑 Roles'],['categories','📂 Categories']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === id ? 'bg-brand-600 text-white' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >{label}</button>
        ))}
      </div>

      {tab === 'plants'     && <PlantsTab />}
      {tab === 'users'      && <UsersTab />}
      {tab === 'roles'      && <RolesTab />}
      {tab === 'categories' && <CategoriesTab />}
    </div>
  );
}
